import React, { useMemo } from "react";
import Loader from "../../components/Loader";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import { useSelector } from "react-redux";
import { useGetProtectedApiDataQuery } from "../../redux/services/apiSlice";
import {
  extractDateFromStr,
  getLatestDateFromData,
  getLatestUpdatedObject,
} from "../../Utils/utils";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import { useTableResponsive } from "../../hooks/useTableResponsive";
import useSeo from "../../hooks/useSeo";
import { useLocation } from "react-router-dom";

const ProjectsInPipelinePage = () => {
  const { getText } = useLanguageTranslation();
  const { languageType } = useSelector((state) => state.homeSlice);
  const { data: pipelineData = [], isLoading: pipelineDataIsLoading } =
    useGetProtectedApiDataQuery({ url: "pipeline", lang: languageType });

  const categoryWiseData = (arr, category) => {
    return arr.filter(
      (item) => item.field_projects_in_pipeline_type === category,
    );
  };

  const hydroType = categoryWiseData(pipelineData, getText("Hydro", "जल"));

  const floatingSolarType = categoryWiseData(
    pipelineData,
    getText("Floating Solar Project", "फ्लोटिंग सोलर प्रोजेक्ट"),
  );
  const pumpedStorageType = categoryWiseData(
    pipelineData,
    getText("Pumped Storage Project", "पंप्ड स्टोरेज प्रोजेक्ट"),
  );

  useTableResponsive(pipelineData);

  const lastUpdatedOn = useMemo(() => {
    return getLatestDateFromData(pipelineData);
  }, [pipelineData]);

  // for SEO
  const { pathname } = useLocation();
  const { innerBannerTitle, innerBannerImage } = useSeo({ pathname });

  if (pipelineDataIsLoading) {
    return <Loader />;
  }

  const renderProjectTable = (projects, title) => {
    if (projects.length === 0) return null;

    return (
      <>
        <div className="title-sec">
          <h2 className="mt-2">{title}</h2>
        </div>
        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-12">
            <div className="table-responsive">
              <table className="table table-striped custom-table">
                <thead className="thead-dark">
                  <tr>
                    <th className="no-wrap-td">
                      {getText("S.No.", "क्र. सं.")}
                    </th>
                    <th>{getText("Name of the Project", "परियोजना का नाम")}</th>
                    <th>{getText("State", "राज्य")}</th>
                    <th>{getText("Status", "स्थिति")}</th>
                    <th className="no-wrap-td">
                      {getText("Commissioning Schedule", "आयोग शेड्यूल")}
                      <br />
                      {getText(
                        "(tent. from Investment Approval)",
                        "(निवेश स्वीकृति से अनुमानित)",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((item, index) => (
                    <tr key={index}>
                      <td data-key={getText("S.No.", "क्र. सं.")}>
                        {index + 1}
                      </td>
                      <td
                        data-key={getText(
                          "Name of the Project",
                          "परियोजना का नाम",
                        )}
                      >
                        {item.title}
                      </td>
                      <td data-key={getText("State", "राज्य")}>
                        {item.field_state}
                      </td>
                      <td data-key={getText("Status", "स्थिति")}>
                        <div
                          dangerouslySetInnerHTML={{ __html: item.body }}
                        ></div>
                      </td>
                      <td
                        className="no-wrap-td"
                        data-key={getText(
                          "Commissioning schedule (tent. from investment approval)",
                          "कमीशनिंग शेड्यूल (निवेश स्वीकृति से अनुमानित)",
                        )}
                      >
                        {item.field_commissioning_schedule_ten}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <InnerBannerBreadcrumb
        bannerImage={innerBannerImage}
        title={getText("Power Generation", "विद्युत उत्पादन")}
        titleLink="/power-generation"
        levelTwo={true}
        titleTwo={innerBannerTitle}
        levelThree={false}
        isActive={innerBannerTitle}
      />

      <div className="inner-container">
        <div className="container">
          <div className="">
            <div className="title-sec">
              <h2>
                {getText(
                  "Projects to be Taken Up",
                  "लिये जाने वाले प्रोजेक्ट्स",
                )}
              </h2>
            </div>
            <p>
              {getText(
                "Future projects of NEEPCO in ownership basis are as follows:",
                "स्वामित्व के आधार पर नीपको के भविष्य के प्रोजेक्ट्स इस प्रकार हैं:",
              )}
            </p>
          </div>
          {renderProjectTable(hydroType, getText("Hydro", "जल"))}
          {renderProjectTable(
            pumpedStorageType,
            getText("Pumped Storage Project", "पंप्ड स्टोरेज प्रोजेक्ट"),
          )}
          {renderProjectTable(
            floatingSolarType,
            getText("Floating Solar Project", "फ्लोटिंग सोलर प्रोजेक्ट"),
          )}
          <div className="mt-4">
            <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectsInPipelinePage;
